import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface MasterService {
  id: number;
  serviceName: string;
  sortOrder: number;
  status: boolean | string;
}

export default function MasterServices() {
  const navigate = useNavigate();
  const [services, setServices] = useState<MasterService[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getServices();
  }, [page, limit, search]);

  const getServices = async () => {
    setLoading(true);
    const response = await request.post("/api/getMasterServices", {
      search,
      limit,
      offset: page,
      orderColumn: "sortOrder",
      orderDirection: "desc",
    });
    setServices(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "serviceName",
      label: "Service Name",
      minWidth: 200,
      format: (value) => value,
    },
    {
      id: "sortOrder",
      label: "Sort Order",
      minWidth: 120,
      align: "center",
      format: (value) => value ?? "-",
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true || value === "Active"} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/master-services/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.serviceName }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Master Services"
        showSearch
        searchPlaceholder="Search services..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Service",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/master-services/create");
          },
        }}
      />

      <DataTable
        columns={columns}
        rows={services}
        totalCount={totalCount}
        onPaginationChange={handlePaginationChange}
        page={page}
        limit={limit}
      />
      <DeleteConfirmation
        open={deleteDialog.open}
        title={"Delete Service - " + deleteDialog?.title}
        message="Are you sure you want to delete this service? This action cannot be undone."
        apiEndpoint={"api/deleteMasterService/"}
        deleteId={deleteDialog.id}
        onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
        refetchApi={() => {
          setPage(0);
          getServices();
        }}
      />
    </PageWrapper>
  );
}
